import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { vaiTroRepository } from '../../../lib/repositories/vai-tro.repository';
import { useToast } from '../../../lib/toast';
import { MOT_TRANG } from '../constants';
import type { Permission, Role, RoleCreate } from '../types';

/**
 * Khoá cache. Vai trò của từng người nằm ở nhánh riêng `vai-tro-nguoi-dung`,
 * **không** dưới `['users']`: nhánh đó còn chứa `me/context`, vô hiệu hoá nó
 * là tính lại quyền cả cổng (xem `KHOA_NGUOI_DUNG`).
 */
export const KHOA_VAI_TRO = {
  danhSach: ['roles', 'danh-sach'] as const,
  danhMucQuyen: ['permissions', 'danh-muc'] as const,
  cuaNguoi: (userId: string) => ['vai-tro-nguoi-dung', userId] as const,
  moiNguoi: ['vai-tro-nguoi-dung'] as const,
};

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
export function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Trần số trang khi nối: chặn vòng lặp nếu server trả `next_cursor` lặp lại. */
const TRANG_TOI_DA = 50;

/** Cỡ trang lớn nhất API danh mục quyền nhận (ADR-0041). */
const TRANG_DANH_MUC = 100;

/**
 * Toàn bộ vai trò của tổ chức. Nối `next_cursor` tới hết rồi tìm ở client:
 * tổ chức có vài chục vai trò, và API chưa có tham số tìm theo tên — cùng cách
 * màn Tenant làm (README, "Phân hệ Tenant").
 */
export function useDanhSachVaiTro() {
  return useQuery<Role[], ApiError>({
    queryKey: KHOA_VAI_TRO.danhSach,
    queryFn: async () => {
      const ra: Role[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await vaiTroRepository.danhSach({ limit: MOT_TRANG, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Danh mục quyền gán được. Đổi khi tổ chức cài hay gỡ module — hiếm, nên giữ lâu. */
export function useDanhMucQuyen(bat = true) {
  return useQuery<Permission[], ApiError>({
    queryKey: KHOA_VAI_TRO.danhMucQuyen,
    queryFn: async () => {
      const ra: Permission[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await vaiTroRepository.danhMucQuyen({ limit: TRANG_DANH_MUC, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 5 * 60_000,
    retry: thuLai,
    enabled: bat,
  });
}

/** Ghi vai trò mới trả về vào danh sách trong cache — dòng đổi tại chỗ, không tải lại. */
function useGhiVaiTro() {
  const queryClient = useQueryClient();
  return (moi: Role) =>
    queryClient.setQueryData<Role[]>(KHOA_VAI_TRO.danhSach, (cu) =>
      cu?.map((r) => (r.id === moi.id ? moi : r)),
    );
}

/**
 * `404` (vai trò vừa bị xoá ở phiên khác) và `422` (quyền vừa không gán được
 * vì module bị gỡ): tải lại danh sách và danh mục để màn nói đúng hiện trạng.
 */
function useKhiLechDuLieu() {
  const queryClient = useQueryClient();
  return (err: ApiError) => {
    if (err.status === 404) void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
    if (err.status === 422) void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhMucQuyen });
  };
}

/**
 * Tạo vai trò. `Idempotency-Key` mới cho **mỗi lần bấm** (hợp đồng G2-D1-4):
 * lần bấm lại sau lỗi mạng mà lần trước đã tới server thì nhận `409` trùng mã,
 * không thành hai vai trò.
 */
export function useTaoVaiTro() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<Role, ApiError, RoleCreate>({
    mutationFn: (du) => vaiTroRepository.tao(du, crypto.randomUUID()),
    onSuccess: (moi) => {
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
      toast.success(t('vaiTroLop2.daTao', { ten: moi.name }));
    },
  });
}

/** Đổi tên. Lỗi không bật toast: form hiện lỗi ngay trong hộp. */
export function useDoiTenVaiTro() {
  const toast = useToast();
  const { t } = useT();
  const ghi = useGhiVaiTro();
  const khiLech = useKhiLechDuLieu();
  return useMutation<Role, ApiError, { id: string; name: string }>({
    mutationFn: ({ id, name }) => vaiTroRepository.doiTen(id, name),
    onSuccess: (moi) => {
      ghi(moi);
      toast.success(t('vaiTroLop2.daDoiTen', { ten: moi.name }));
    },
    onError: khiLech,
  });
}

/** Thay cả tập quyền của một vai trò. */
export function useGanQuyen() {
  const toast = useToast();
  const { t } = useT();
  const ghi = useGhiVaiTro();
  const khiLech = useKhiLechDuLieu();
  return useMutation<Role, ApiError, { id: string; permissions: string[] }>({
    mutationFn: ({ id, permissions }) => vaiTroRepository.ganQuyen(id, permissions),
    onSuccess: (moi) => {
      ghi(moi);
      toast.success(t('vaiTroLop2.daLuuQuyen', { ten: moi.name }));
    },
    onError: khiLech,
  });
}

/**
 * Xoá vai trò. Server gỡ vai trò khỏi mọi người đang có, nên vai trò của từng
 * người trong cache (kèm `ETag`) đều đã cũ: bỏ hết, lần mở ngăn sau đọc lại.
 */
export function useXoaVaiTro() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const khiLech = useKhiLechDuLieu();
  return useMutation<void, ApiError, Role>({
    mutationFn: (r) => vaiTroRepository.xoa(r.id),
    onSuccess: (_kq, r) => {
      queryClient.setQueryData<Role[]>(KHOA_VAI_TRO.danhSach, (cu) => cu?.filter((x) => x.id !== r.id));
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhSach });
      queryClient.removeQueries({ queryKey: KHOA_VAI_TRO.moiNguoi });
      toast.success(t('vaiTroLop2.daXoa', { ten: r.name }));
    },
    onError: khiLech,
  });
}
